import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { ShoppingBag } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useNetwork } from '../../contexts/NetworkContext'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { CardContent, CardIcon } from '@/components/ui/card'
import { AuthService } from '@/services/core/auth'
import { OrdersApi, Order } from '@/services/orders/ordersApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { OrderTable } from '../../components/OrderTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import {
  DEFAULT_ORDER_FILTERS,
  OrderFilters,
  orderFetchFilters,
  type OrderFilterState,
} from '../../components/OrderFilters'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { ListPagination } from '../../components/ListPagination'
import { getContext } from '@/services/networks/networksApi'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function OrdersPage() {
  const { t } = useTranslation()
  const { scopeKey, scopeReady, currentNetwork } = useNetwork()
  // A marketplace order belongs to an advertiser, so it's created from one.
  const canCreateOrder = !currentNetwork?.marketplacePlatform
  const hasToken = AuthService.isAuthenticated()
  const queryClient = useQueryClient()
  const [stored, setFilters] = usePersistedState<OrderFilterState>('filters.orders', DEFAULT_ORDER_FILTERS)
  // A remembered value predates any filter added since, so the defaults fill in the rest.
  const filters: OrderFilterState = { ...DEFAULT_ORDER_FILTERS, ...stored }
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<Order[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const includeInactive = filters.objectState !== 'ACTIVE'
  const context = getContext()

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    OrdersApi.getPage(page, pageSize, includeInactive, search, sort, orderFetchFilters(filters))

  const { data, isLoading } = useQuery({
    queryKey: ['orders', context, scopeKey, filters, search, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: hasToken && scopeReady,
  })

  const orders = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const handleFilters = (v: OrderFilterState) => {
    setFilters(v)
    setPage(1)
    setSelection([])
  }

  const handleSearch = (v: string) => {
    setSearch(v)
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((o) => OrdersApi.update(o.id, { name: o.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['orders', context] })
      toast.success(t('common.done'), { duration: 3000 })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('orders.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('orders.title')}</h1>
            <p>{t('orders.subtitle')}</p>
          </div>
          {canCreateOrder && (
            <div className="ad-page__actions">
              <CreateObjectButton type="order" size="default" />
            </div>
          )}
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)}>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={ShoppingBag} />
                {t('orders.orderCount', { count: totalCount })}
              </>
            }
            controls={
              <>
                <SearchInput value={search} onChange={handleSearch} />
                <OrderFilters filters={filters} onChange={handleFilters} />
              </>
            }
            actions={<PageSizeSelect value={pageSize} onChange={(s) => { setPageSize(s); setPage(1) }} />}
          />

          {selection.length > 0 && (
            <BulkActionBar
              count={selection.length}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
              onClear={() => setSelection([])}
            />
          )}

          <CardContent className="p-0">
            <OrderTable
              orders={orders}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('orders.orderCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
